/**
 * Locale-aware navigation for the language switcher (SPA-safe).
 */

import { GUIDE_PAGES, BLOG_POSTS_EXPANDED } from '@/lib/seo/content-network';
import {
  cityPath,
  parseCityLandingPath,
  type SEOLocale,
  localeFromI18nCode,
} from '@/lib/seo/locale-seo';

const stripTrailingSlash = (p: string): string => p.replace(/\/$/, '') || '/';

const mapGuideSlug = (slug: string, target: SEOLocale): string | null => {
  const guide = GUIDE_PAGES.find(g => g.slug === slug || g.slugEn === slug);
  if (!guide) return null;
  if (target === 'en') return `/guide/${guide.slugEn}`;
  return `/guide/${guide.slug}`;
};

const mapBlogSlug = (slug: string, target: SEOLocale): string | null => {
  const post = BLOG_POSTS_EXPANDED.find(p => p.slug === slug || p.slugEn === slug);
  if (!post) return null;
  if (target === 'en') return `/blog/${post.slugEn}`;
  return `/blog/${post.slug}`;
};

const mapProgrammaticHub = (path: string, target: SEOLocale): string | null => {
  const hubs: Record<string, { fr: string; en: string; ar: string }> = {
    '/fr/location-voiture': { fr: '/fr/location-voiture', en: '/en/car-rental', ar: '/ar/location-voiture' },
    '/en/car-rental': { fr: '/fr/location-voiture', en: '/en/car-rental', ar: '/ar/location-voiture' },
    '/ar/location-voiture': { fr: '/fr/location-voiture', en: '/en/car-rental', ar: '/ar/location-voiture' },
    '/fr/location-voiture/longue-duree': {
      fr: '/fr/location-voiture/longue-duree',
      en: '/en/car-rental/long-term',
      ar: '/ar/location-voiture/longue-duree',
    },
    '/en/car-rental/long-term': {
      fr: '/fr/location-voiture/longue-duree',
      en: '/en/car-rental/long-term',
      ar: '/ar/location-voiture/longue-duree',
    },
    '/ar/location-voiture/longue-duree': {
      fr: '/fr/location-voiture/longue-duree',
      en: '/en/car-rental/long-term',
      ar: '/ar/location-voiture/longue-duree',
    },
    '/fr/location-voiture/pas-cher': {
      fr: '/fr/location-voiture/pas-cher',
      en: '/en/car-rental/cheap',
      ar: '/ar/location-voiture/pas-cher',
    },
    '/en/car-rental/cheap': {
      fr: '/fr/location-voiture/pas-cher',
      en: '/en/car-rental/cheap',
      ar: '/ar/location-voiture/pas-cher',
    },
    '/ar/location-voiture/pas-cher': {
      fr: '/fr/location-voiture/pas-cher',
      en: '/en/car-rental/cheap',
      ar: '/ar/location-voiture/pas-cher',
    },
    '/fr/location-voiture/luxe': {
      fr: '/fr/location-voiture/luxe',
      en: '/en/car-rental/luxury',
      ar: '/ar/location-voiture/luxe',
    },
    '/en/car-rental/luxury': {
      fr: '/fr/location-voiture/luxe',
      en: '/en/car-rental/luxury',
      ar: '/ar/location-voiture/luxe',
    },
    '/ar/location-voiture/luxe': {
      fr: '/fr/location-voiture/luxe',
      en: '/en/car-rental/luxury',
      ar: '/ar/location-voiture/luxe',
    },
  };
  const row = hubs[path];
  return row ? row[target] : null;
};

/**
 * Resolve target pathname (+ preserve search) when switching UI language.
 * Returns null if only i18n toggle is possible (no mapped SEO URL).
 */
export const resolveLocaleSwitchTarget = (
  pathname: string,
  search: string,
  targetLangCode: string
): string | null => {
  const target = localeFromI18nCode(targetLangCode);
  const path = stripTrailingSlash(pathname);

  const city = parseCityLandingPath(path);
  if (city) {
    return `${cityPath(city.citySlug, target)}${search}`;
  }

  const hub = mapProgrammaticHub(path, target);
  if (hub) {
    return `${hub}${search}`;
  }

  // Home has a single route (/); language switch is i18n-only (no SEO hub redirect).
  if (path === '/' || path === '/en' || path === '/ar') {
    return null;
  }

  const guideM = path.match(/^\/guide\/([^/]+)$/);
  if (guideM) {
    const mapped = mapGuideSlug(guideM[1], target);
    if (mapped) return `${mapped}${search}`;
    if (target === 'ar') return null;
  }

  const blogM = path.match(/^\/blog\/([^/]+)$/);
  if (blogM) {
    const mapped = mapBlogSlug(blogM[1], target);
    if (mapped) return `${mapped}${search}`;
    if (target === 'ar') return null;
  }

  return null;
};
